import { Grid, LinearProgress, Typography, Box, Tabs, Tab, Stack } from '@mui/material'
import React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import schema from '../../schemas/simulation.json'
import axios, { AxiosError } from 'axios'
import { useQuery } from 'react-query'
import { JSONSchema7 } from 'json-schema'
import { format } from 'date-fns'
import Config from './tabs/Config'
import Results from './tabs/Results'
import Raw from './tabs/Raw'
import { useAuth } from '../../context/useAuth'
import CircularProgressWithLabel from '../shared/CircularProgressWithLabel'
import EditSimulation from '../simulations/EditSimulation'
import Scenarios from './tabs/Scenarios'

function useSimulation(name: string) {
	const { auth, setAuth, getCSRF } = useAuth()
	return useQuery<any, AxiosError>({
		refetchOnWindowFocus: false,
		queryKey: ['simulation'],
		queryFn: async () => {
			try {
				const res = await axios.get(`${process.env.REACT_APP_API}/${name}`, {
					withCredentials: true
				})
				return res.data
			} catch (err: any) {
				const error = err
				if (error.response.status === 401) {
					setAuth({ ...auth, isAuthenticated: false })
					getCSRF()
				}
			}
		}
	})
}

export default function Simulation() {
	const { name } = useParams()
	const { simulation, setSimulation } = useAuth()
	const [searchParams] = useSearchParams()
	const { status, data } = useSimulation(name!)
	const [tab, setTab] = React.useState<string>(searchParams.get('tab') || 'config')

	React.useEffect(() => {
		if (data && status === 'success') {
			setSimulation(data)
		}
	}, [status, data, setSimulation])

	if (!simulation || name !== simulation.simulation_name) return <LinearProgress />

	return (
		<Stack>
			<Box
				sx={{
					borderBottom: 1,
					borderColor: 'divider',
					width: '100%',
					position: 'fixed',
					backgroundColor: 'background.default',
					zIndex: 1000
				}}
			>
				<Tabs
					sx={{ minHeight: 32 }}
					value={tab}
					onChange={(e, newValue) => {
						setTab(newValue)
						window.history.replaceState(null, '', `?tab=${newValue}`)
					}}
				>
					<Tab label='Config' value={'config'} />
					<Tab label='Results' value={'results'} />
					<Tab label='Scenarios' value={'scenarios'} />
					<Tab label='Raw' value={'raw'} />
				</Tabs>
			</Box>
			<Stack
				sx={{
					width: '100%',
					mt: 4
				}}
			>
				<Grid container sx={{ p: 2, pb: 1 }} alignItems={'center'}>
					{simulation?.status?.running && (
						<CircularProgressWithLabel value={Math.round(simulation?.status.progress * 100.0)} />
					)}
					<Stack>
						<Typography sx={{ fontSize: 16 }}>{name}</Typography>
						<Typography sx={{ fontSize: 12 }} color='GrayText'>{`${format(
							new Date(simulation?.start_time),
							'PP'
						)} → ${format(new Date(simulation?.end_time), 'PP')} (freq: ${simulation?.ems_frequency}s)`}</Typography>
					</Stack>

					{tab === 'config' && (
						<Grid item sx={{ position: 'absolute', right: 16 }}>
							<EditSimulation
								disabled={simulation?.status?.running}
								data={simulation}
								schema={schema as JSONSchema7}
								value={simulation}
								setData={setSimulation}
							/>
						</Grid>
					)}
				</Grid>

				<Config visible={tab === 'config'} simulation={simulation} setSimulation={setSimulation} />
				<Results key={name} visible={tab === 'results'} />
				<Scenarios visible={tab === 'scenarios'} simulation={simulation} setSimulation={setSimulation} />
				<Raw visible={tab === 'raw'} simulation={simulation} setSimulation={setSimulation} />
			</Stack>
		</Stack>
	)
}
